import { useEffect, useRef } from "react";
import type { Message } from "../../shared/types";
import { StatusMessage } from "./StatusMessage";
import { MarkdownMessage } from "./MarkdownMessage";
import { ErrorMessage } from "./ErrorMessage";
import { trackWidgetEvent } from "../../telemetry/events";

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  statusMessage?: string | null;
  primaryColor: string;
  conversationId: string | null;
  sourceUrl?: string;
  agentId: string;
  organizationId?: string;
  onRetryMessage?: (messageId: string) => void;
}

export function MessageList({
  messages,
  isLoading,
  statusMessage,
  primaryColor,
  conversationId,
  sourceUrl,
  agentId,
  organizationId,
  onRetryMessage,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const lastMessage = messages[messages.length - 1];
  const isStreamingWithContent =
    lastMessage?.role === "assistant" && lastMessage.content.trim().length > 0;
  const shouldShowStatus = isLoading && !isStreamingWithContent;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        ref={containerRef}
        className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5"
        role="log"
        aria-label="Chat messages"
      >
        {messages.map((message) => {
          const isUser = message.role === "user";
          const hasContent = message.content.trim().length > 0;

          if (!hasContent && !isUser) {
            return null;
          }

          return (
            <div
              key={message.id}
              role="article"
              aria-label={`Message from ${message.role}`}
              className={`flex ${isUser ? "justify-end" : "flex-col items-start"}`}
            >
              {isUser ? (
                <div className="max-w-[85%] rounded-3xl bg-[#E0E0E0] px-6 py-4 text-[#232323]">
                  <p className="whitespace-pre-wrap wrap-break-word text-base leading-[1.21]">
                    {message.content}
                  </p>
                </div>
              ) : message.error ? (
                <ErrorMessage
                  content={message.content}
                  retryable={message.retryable ?? false}
                  onRetry={
                    message.retryable && onRetryMessage
                      ? () => onRetryMessage(message.id)
                      : undefined
                  }
                  primaryColor={primaryColor}
                />
              ) : (
                <MarkdownMessage
                  content={message.content}
                  onLinkClick={(url, linkText) => {
                    const linkClickedAttributes: Record<string, string> = {
                      url,
                      linkText,
                      messageId: message.id,
                      sourceUrl: sourceUrl || "unknown",
                      agentId,
                    };
                    if (conversationId) {
                      linkClickedAttributes.conversationId = conversationId;
                    }
                    if (organizationId) {
                      linkClickedAttributes.organizationId = organizationId;
                    }
                    trackWidgetEvent("link.clicked", linkClickedAttributes);
                  }}
                />
              )}
            </div>
          );
        })}
        {shouldShowStatus && (
          <div className="py-4" aria-hidden="true">
            <StatusMessage
              message={statusMessage}
              primaryColor={primaryColor}
            />
          </div>
        )}
      </div>
      <div role="status" className="sr-only">
        {shouldShowStatus && statusMessage}
      </div>
    </div>
  );
}
